import { useMemo } from 'react';
import type { ComponentProps } from 'react';
import { Box } from '@mui/material';
import { IconInfoCircle } from '@tabler/icons-react';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { DateRange } from '../../discover-dashboard-page/types';
import InsightCard from '../../solve-insights/common/InsightCard';
import { DONUT_SIZE } from '../../solve-insights/constants';
import { numbersToStringWithCommas } from '../../solve-insights/helpers';
import DonutGraph from 'src/components/solve-insights-charts/donut-graph.tsx/DonutGraph';
import { DonutGraphDataType } from 'src/services/insights/types';

interface PieChartCardProps {
  centerSubText?: string;
  centerText: string;
  colors: string[];
  dateRange: DateRange;
  donutData: DonutGraphDataType;
  isLoading: boolean;
  legendData?: DonutGraphDataType;
  showSecondaryLabel?: boolean;
  size?: 'large' | 'small';
  title: string;
  tooltip: ComponentProps<typeof Tooltip>['tooltipContent'];
}

const PieChartCard = ({
  centerSubText,
  centerText,
  colors,
  donutData,
  isLoading,
  legendData,
  showSecondaryLabel = false,
  size = 'small',
  title,
  tooltip,
}: PieChartCardProps) => {
  const showComparison = true;

  // Use legendData for the legend if provided, otherwise fall back to donutData
  const legendValues = useMemo(
    () => (legendData ?? donutData).values,
    [legendData, donutData],
  );

  return (
    <InsightCard
      isFetching={isLoading}
      isLoading={isLoading}
      size={size}
      title={title}
      tooltip={
        tooltip && (
          <Tooltip maxWidth={320} tooltipContent={tooltip}>
            <IconInfoCircle color='currentColor' height={16} width={16} />
          </Tooltip>
        )
      }
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: legendValues && legendValues.length > 3 ? '6px' : '16px',
            justifyContent: 'center',
          }}
        >
          {legendValues?.map((legendValue, index) =>
            legendValues.length > 3 ? (
              <Box
                key={index}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    bgcolor: colors[index],
                    height: '12px',
                    minWidth: '12px',
                  }}
                />
                <Typography variant='font14Bold'>
                  {showSecondaryLabel && legendValue.secondaryLabel
                    ? legendValue.secondaryLabel
                    : numbersToStringWithCommas({
                        number: legendValue.value,
                      })}
                </Typography>
                <Typography variant='font14'>
                  {legendValue.label.toLowerCase()}
                </Typography>
              </Box>
            ) : (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <Typography variant='font16Bold'>
                  {showSecondaryLabel && legendValue.secondaryLabel
                    ? legendValue.secondaryLabel
                    : numbersToStringWithCommas({
                        number: legendValue.value,
                      })}
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '4px',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: colors[index],
                      height: '12px',
                      width: '12px',
                    }}
                  />
                  <Typography variant='font14'>
                    {legendValue.label.toLowerCase()}
                  </Typography>
                </Box>
              </Box>
            ),
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: showComparison ? 0 : 1,
          }}
        >
          <DonutGraph
            centerSubText={centerSubText}
            centerText={centerText}
            colors={colors}
            containerProps={{
              style: { height: `${DONUT_SIZE}px`, width: `${DONUT_SIZE}px` },
            }}
            data={donutData}
            disableTooltip
          />
        </Box>
      </Box>
    </InsightCard>
  );
};

export default PieChartCard;
